import { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Quiz de 3 passos que cruza a necessidade do visitante com a "entrega" de cada
 * pousada (perfil, comodidades, vila, capacidade, restaurante, nota) e recomenda
 * 1 a 3 pousadas com o perfil mais próximo. 100% client-side.
 */

type Answers = { companhia?: string; prioridade?: string; clima?: string };

const STEPS = [
  {
    key: 'companhia' as const,
    title: 'Com quem você vai viajar?',
    options: [
      { v: 'casal', label: 'Casal', icon: '💑' },
      { v: 'familia', label: 'Família com crianças', icon: '👨‍👩‍👧' },
      { v: 'mochileiro', label: 'Sozinho ou mochileiro', icon: '🎒' },
      { v: 'amigos', label: 'Grupo de amigos', icon: '🎉' },
    ],
  },
  {
    key: 'prioridade' as const,
    title: 'O que mais importa pra você?',
    options: [
      { v: 'conforto', label: 'Conforto e tranquilidade', icon: '✨' },
      { v: 'preco', label: 'Custo-benefício', icon: '💰' },
      { v: 'mar', label: 'Ficar pertinho do mar', icon: '🌊' },
      { v: 'gastronomia', label: 'Boa gastronomia', icon: '🍽️' },
    ],
  },
  {
    key: 'clima' as const,
    title: 'Que clima você procura?',
    options: [
      { v: 'qualquer', label: 'Tanto faz, quero a melhor', icon: '⭐' },
      { v: 'encantadas', label: 'Agito e vida de vila', icon: '🔥' },
      { v: 'nova-brasilia', label: 'Central e prático', icon: '📍' },
      { v: 'sossego', label: 'Paz e natureza', icon: '🌿' },
    ],
  },
];

function notaNorm(p: any): number {
  if (p.rating?.booking) return p.rating.booking / 2;
  if (p.rating?.tripAdvisor) return p.rating.tripAdvisor;
  if (p.rating?.google) return p.rating.google;
  return 0;
}
function maxCap(p: any): number {
  const caps = (p.roomTypes || []).map((r: any) => {
    const m = /(\d+)/.exec(String(r.capacity ?? ''));
    return m ? parseInt(m[1], 10) : 0;
  });
  return caps.length ? Math.max(...caps) : 0;
}

function score(p: any, a: Answers): number {
  let s = 0;
  const am: string[] = p.amenities || [];
  const has = (x: string) => am.includes(x);
  const nota = notaNorm(p);
  const cap = maxCap(p);
  const eco = p.perfil === 'economico';
  const luxo = p.perfil === 'luxo';
  const medio = p.perfil === 'medio';
  const simples = /hostel|camping/i.test(p.title || '');

  // 1) companhia
  if (a.companhia === 'casal') { if (luxo) s += 3; else if (medio) s += 1.5; if (has('vista-mar') || has('varanda')) s += 1; if (simples) s -= 3; }
  if (a.companhia === 'familia') { if (cap >= 4) s += 3; if (has('cafe-da-manha')) s += 1; if (simples) s -= 1.5; }
  if (a.companhia === 'mochileiro') { if (eco) s += 3; if (has('cozinha-compartilhada')) s += 2; if (luxo) s -= 2; }
  if (a.companhia === 'amigos') { if (!luxo) s += 1; if (cap >= 4) s += 2; if (has('cozinha-compartilhada') || has('churrasqueira')) s += 1; }

  // 2) prioridade
  if (a.prioridade === 'conforto') { if (luxo) s += 3; else if (medio) s += 1; if (nota >= 4.5) s += 2; }
  if (a.prioridade === 'preco') { if (eco) s += 3; else if (medio) s += 1.5; if (nota >= 4.2) s += 1; }
  if (a.prioridade === 'mar') { if (has('pe-na-areia')) s += 3; if (has('vista-mar')) s += 2; }
  if (a.prioridade === 'gastronomia') { if (p.restaurante?.nome) s += 4; }

  // 3) clima / vila
  if (a.clima === 'encantadas' && p.vilarejo === 'encantadas') s += 3;
  if (a.clima === 'nova-brasilia' && p.vilarejo === 'nova-brasilia') s += 3;
  if (a.clima === 'sossego' && (p.vilarejo === 'farol' || p.vilarejo === 'praia-grande')) s += 3;
  if (a.clima === 'qualquer') s += nota >= 4.6 ? 2 : nota >= 4.3 ? 1 : 0;

  // desempate por reputação + destaque
  s += nota >= 4.6 ? 1.5 : nota >= 4.3 ? 0.8 : 0;
  if (p.featured) s += 0.5;
  return s;
}

function reasons(p: any, a: Answers): string[] {
  const out: string[] = [];
  const labelComp: Record<string, string> = { casal: 'casais', familia: 'famílias', mochileiro: 'mochileiros', amigos: 'grupos' };
  if (a.companhia) out.push(`Ideal para ${labelComp[a.companhia]}`);
  if (a.prioridade === 'gastronomia' && p.restaurante?.nome) out.push('Restaurante próprio');
  if (a.prioridade === 'conforto' && p.perfil === 'luxo') out.push('Perfil premium');
  if (a.prioridade === 'preco' && (p.perfil === 'economico' || p.perfil === 'medio')) out.push('Bom custo-benefício');
  if (a.prioridade === 'mar' && ((p.amenities || []).includes('pe-na-areia') || (p.amenities || []).includes('vista-mar'))) out.push('Pertinho do mar');
  out.push(p.vilarejoName);
  return out.filter(Boolean).slice(0, 3);
}

function notaLabel(p: any): string | null {
  if (p.rating?.booking) return `${Number(p.rating.booking).toFixed(1)} Booking`;
  if (p.rating?.tripAdvisor) return `${Number(p.rating.tripAdvisor).toFixed(1)} TripAdvisor`;
  return null;
}

export function PousadaQuiz({ pousadas }: { pousadas: any[] }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [done, setDone] = useState(false);

  const pick = (key: string, v: string) => {
    const next = { ...answers, [key]: v };
    setAnswers(next);
    if (step < STEPS.length - 1) setStep(step + 1);
    else setDone(true);
  };
  const reset = () => { setAnswers({}); setStep(0); setDone(false); };

  let results: any[] = [];
  if (done) {
    const ranked = pousadas
      .map(p => ({ p, s: score(p, answers) }))
      .sort((a, b) => b.s - a.s);
    const top = ranked[0]?.s ?? 0;
    let group = ranked.filter(r => r.s >= top - 1.6).slice(0, 3);
    const PARCEIRA = 'pousada-fim-da-trilha';
    const parceira = ranked.find(r => r.p.handle === PARCEIRA);
    // Regra: "casal" + "gastronomia" → Fim da Trilha SEMPRE em 1º lugar.
    const forcarTopo = answers.companhia === 'casal' && answers.prioridade === 'gastronomia';
    if (forcarTopo && parceira) {
      group = [parceira, ...group.filter(r => r.p.handle !== PARCEIRA)].slice(0, 3);
    } else if (!group.some(r => r.p.handle === PARCEIRA)) {
      // Parceira sempre recomendada — entra como última opção quando não está no topo.
      if (parceira) group = [...group.slice(0, 2), parceira];
    }
    results = group.map(r => r.p);
  }

  return (
    <section className="mb-16 bg-teal-deep -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-12 rounded-2xl" id="quiz">
      <div className="max-w-3xl mx-auto text-center">
        <span className="font-body text-sm text-sand-200 font-semibold uppercase tracking-wider">Teste rápido</span>
        <h2 className="font-display text-3xl sm:text-4xl text-white mt-1 mb-2">Qual pousada combina com você?</h2>
        <p className="font-body text-sand-100/90 mb-8">Responda 3 perguntas e eu indico a pousada com a sua cara.</p>

        {!done ? (
          <div className="bg-white rounded-2xl p-6 sm:p-8 text-left shadow-lg">
            <div className="flex items-center gap-2 mb-5">
              {STEPS.map((_, i) => (
                <div key={i} className={`h-1.5 flex-1 rounded-full ${i <= step ? 'bg-coral' : 'bg-sand-200'}`} />
              ))}
            </div>
            <p className="font-body text-xs text-warm-gray mb-1">Passo {step + 1} de {STEPS.length}</p>
            <h3 className="font-display text-xl sm:text-2xl text-warm-dark mb-5">{STEPS[step].title}</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {STEPS[step].options.map(opt => (
                <button
                  key={opt.v}
                  onClick={() => pick(STEPS[step].key, opt.v)}
                  className="flex items-center gap-3 p-4 rounded-xl border border-sand-200 hover:border-coral hover:bg-coral/5 transition-all text-left"
                >
                  <span className="text-2xl flex-shrink-0">{opt.icon}</span>
                  <span className="font-body font-medium text-warm-dark">{opt.label}</span>
                </button>
              ))}
            </div>
            {step > 0 && (
              <button onClick={() => setStep(step - 1)} className="mt-5 font-body text-sm text-warm-gray hover:text-warm-dark">
                ← Voltar
              </button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-6 sm:p-8 text-left shadow-lg">
            <p className="font-body text-sm text-coral font-semibold uppercase tracking-wider mb-1">
              {results.length > 1 ? 'Suas melhores opções' : 'A pousada com a sua cara'}
            </p>
            <h3 className="font-display text-2xl text-warm-dark mb-5">Encontrei {results.length === 1 ? 'a pousada certa' : `${results.length} pousadas`} pra você</h3>
            <div className="space-y-4">
              {results.map((p, i) => {
                const n = notaLabel(p);
                return (
                  <div key={p.handle} className={`flex gap-4 p-3 rounded-xl border ${i === 0 ? 'border-coral/40 bg-coral/5' : 'border-sand-200'}`}>
                    <Link to={`/pousada/${p.handle}`} className="block w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={p.image} alt={p.title} className="w-full h-full object-cover" loading="lazy" decoding="async" width={96} height={96} />
                    </Link>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        {i === 0 && <span className="font-body text-[11px] font-bold text-coral uppercase tracking-wide">★ Melhor combinação</span>}
                        {p.type === 'pousada-parceira' && <span className="font-body text-[11px] font-bold text-teal-deep uppercase tracking-wide">✓ Parceira do guia</span>}
                      </div>
                      <h4 className="font-display text-lg text-warm-dark leading-tight">{p.title}</h4>
                      <div className="flex flex-wrap gap-1.5 my-1.5">
                        {reasons(p, answers).map((r, j) => (
                          <span key={j} className="font-body text-[11px] bg-sand-100 text-warm-dark px-2 py-0.5 rounded-full">{r}</span>
                        ))}
                        {n && <span className="font-body text-[11px] bg-teal-light/15 text-teal-deep px-2 py-0.5 rounded-full">⭐ {n}</span>}
                      </div>
                      <Link to={`/pousada/${p.handle}`} className="font-body text-sm font-semibold text-teal-deep hover:underline">
                        Ver detalhes completos da pousada →
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
            <button onClick={reset} className="mt-6 font-body text-sm text-warm-gray hover:text-warm-dark">↻ Refazer o teste</button>
          </div>
        )}
      </div>
    </section>
  );
}
